import { camelCase } from 'change-case';
import slugify from 'slugify';

import { type Masked, unmask } from '~/contentful/graphql';
import { Typename } from '~/contentful/graphql/typename';
import { ArticleType } from '~/contentful/model/articleType';
import { ContentType } from '~/contentful/model/contentType';
import { getGrantSlug } from '~/ui/systems/grant';
import { Route } from '~/ui/systems/internalLink/route';
import { invariant } from '~/utils/invariant';

import { type InternalLink_EntryFragment } from './query';

/**
 * Constructs and returns internal links for different types of Contentful entries.
 *
 * For entries representing root pages like `Contact` or `EventsPage`, this function will
 * return a direct link to these pages.
 *
 * For repeatable entries like `Idea` or `GrantActivityArticle`, this function constructs
 * links including a `slug`, leading to individual instances of these types.
 *
 * @param data An instance of {@link InternalLink_EntryFragment}, representing a linkable entry from Contentful.
 * @throws {invariant.Violation} Throws an error if the entry type isn't linkable or the article subtype is unhandled.
 */
export function getInternalLinkFromFragment(
  data: Masked<typeof InternalLink_EntryFragment>,
): string {
  const entry = unmask<typeof InternalLink_EntryFragment>(data);
  if (!entry?.__typename) {
    console.error('getInternalLinkFromFragment: Entry typename is null', entry);
    return '';
  }
  const args = [camelCase(entry.__typename) as ContentType];
  if (entry.__typename === Typename.ARTICLE_PAGE) {
    if (entry.externalNewsUrl) {
      return entry.externalNewsUrl;
    }
    // @ts-expect-error this is to bypass GraphQL typing
    args.push(entry.type ?? '');
  }

  // if
  if (entry.__typename === Typename.GRANT || entry.__typename === Typename.GRANT_DETAILS_PAGE) {
    // @ts-expect-error args type expects `ContentType`
    args.push(getGrantSlug(entry));
  } else if (
    entry.__typename === Typename.DEPARTMENT ||
    entry.__typename === Typename.GRANTMAKING_AREA
  ) {
    // @ts-expect-error args type expects `ContentType`
    args.push(slugify(entry.name ?? '', { lower: true }));
  } else if (entry.__typename === Typename.ANNUAL_REPORT_PAGE) {
    // @ts-expect-error this is to bypass GraphQL typing
    args.push(entry.year);
  } else if (entry.__typename === Typename.ANNUAL_REPORT_PDF) {
    // @ts-expect-error this is to bypass GraphQL typing
    args.push(entry.report?.url);
  } else {
    // @ts-expect-error this is to bypass GraphQL typing
    args.push(entry.slug);
  }

  // @ts-expect-error this is to bypass GraphQL typing
  return getInternalLink(...args);
}

type ContentTypeWithSlug =
  | ContentType.ANNUAL_REPORT_PAGE
  | ContentType.ANNUAL_REPORT_PDF
  | ContentType.FEATURE
  | ContentType.GRANT
  | ContentType.GRANTMAKING_AREA
  | ContentType.GRANTMAKING_AREA_DETAILS_PAGE
  | ContentType.GRANT_DETAILS_PAGE
  | ContentType.GRANT_STORY
  | ContentType.HISTORY_PERSON
  | ContentType.IDEA_PAGE
  | ContentType.NEWSLETTER_PAGE
  | ContentType.ROOT_PAGE;

type ContentTypeWithoutSlug =
  | ContentType.CONTACT_PAGE
  | ContentType.EVENTS_LANDING_PAGE
  | ContentType.STORIES_LANDING_PAGE
  | ContentType.FINANCIALS_PAGE
  | ContentType.GRANTMAKING_AREAS_PAGE
  | ContentType.GRANT_DATABASE_PAGE
  | ContentType.GRANT_RESOURCES_PAGE
  | ContentType.HOME_PAGE
  | ContentType.RESOURCES_PROSPECTIVE_GRANTEES
  | ContentType.RESOURCES_CURRENT_GRANTEES
  | ContentType.HISTORY_PAGE
  | ContentType.MISSION_PAGE
  | ContentType.MMUF_PAGE
  | ContentType.NEWS_LANDING_PAGE
  | ContentType.PEOPLE_PAGE
  | ContentType.PRESS_ROOM_PAGE
  | ContentType.PRIVACY_POLICY
  | ContentType.TERMS_OF_USE_PAGE
  | ContentType.TRUSTEE
  | ContentType.TRUSTEES_PAGE;

export function getInternalLink(contentType: ContentTypeWithSlug, slug: string): string;
export function getInternalLink(contentType: ContentTypeWithoutSlug, slug?: never): string;
export function getInternalLink(
  contentType: ContentType.ARTICLE_PAGE,
  subType: ArticleType | ArticleType[],
  slug: string,
): string;
export function getInternalLink(
  contentType: ContentTypeWithSlug | ContentTypeWithoutSlug | ContentType.ARTICLE_PAGE,
  slug?: string | ArticleType | ArticleType[],
  articleSlug?: string,
): string {
  switch (contentType) {
    case ContentType.ANNUAL_REPORT_PAGE:
      return `${Route.ANNUAL_REPORT}/${slug}`;

    case ContentType.ANNUAL_REPORT_PDF:
      // slug is the full asset URL here
      return slug as string;

    case ContentType.ARTICLE_PAGE:
      const subType = Array.isArray(slug) ? slug[0] : slug;

      switch (subType) {
        case ArticleType.GENERIC:
          return `${Route.ARTICLE}/${articleSlug}`;

        case ArticleType.STORY:
          return `${Route.STORIES}/${articleSlug}`;

        case ArticleType.GRANTEE:
          return `${Route.NEWS}/${articleSlug}`;

        case ArticleType.MELLON_IN_THE_NEWS:
          return `${Route.NEWS}/${articleSlug}`;

        case ArticleType.PAST_EVENT:
          return `${Route.EVENTS}/${articleSlug}`;

        case ArticleType.REPORT:
          return `${Route.REPORT}/${articleSlug}`;

        case ArticleType.VOICES:
          return `${Route.VOICES}/${articleSlug}`;

        default:
          invariant(false, `Unknown article type: ${subType}`);
      }

    case ContentType.CONTACT_PAGE:
      return Route.CONTACT;

    case ContentType.EVENTS_LANDING_PAGE:
      return Route.EVENTS;

    case ContentType.STORIES_LANDING_PAGE:
      return Route.STORIES;

    case ContentType.FEATURE:
      return `${Route.FEATURE}/${slug}`;

    case ContentType.FINANCIALS_PAGE:
      return Route.FINANCIALS;

    case ContentType.GRANTMAKING_AREA:
      return `${Route.GRANT_MAKING_AREAS}/${slug}`;

    case ContentType.GRANTMAKING_AREAS_PAGE:
      return Route.GRANT_MAKING_AREAS;

    case ContentType.GRANTMAKING_AREA_DETAILS_PAGE:
      return `${Route.GRANT_MAKING_AREAS}/${slug}`;

    case ContentType.GRANT_DATABASE_PAGE:
      return Route.GRANT_SEARCH;

    case ContentType.GRANT_RESOURCES_PAGE:
      return Route.GRANT_RESOURCES;

    case ContentType.HOME_PAGE:
      return Route.HOME;

    case ContentType.RESOURCES_PROSPECTIVE_GRANTEES:
      return Route.RESOURCES_PROSPECTIVE_GRANTEES;

    case ContentType.RESOURCES_CURRENT_GRANTEES:
      return Route.RESOURCES_CURRENT_GRANTEES;

    case ContentType.GRANT_STORY:
      return `${Route.GRANT_STORY}/${slug}`;

    case ContentType.GRANT:
    case ContentType.GRANT_DETAILS_PAGE:
      return `${Route.GRANT_DETAILS}/${slug}`;

    case ContentType.HISTORY_PAGE:
      return Route.HISTORY;

    case ContentType.HISTORY_PERSON:
      return `${Route.HISTORY}/${slug}`;

    case ContentType.IDEA_PAGE:
      return `${Route.IDEA}/${slug}`;

    case ContentType.MISSION_PAGE:
      return Route.MISSION;

    case ContentType.MMUF_PAGE:
      return Route.MMUF;

    case ContentType.NEWSLETTER_PAGE:
      return slug === 'newsletter' ? Route.NEWSLETTER : `${Route.NEWSLETTER}/${slug}`;

    case ContentType.NEWS_LANDING_PAGE:
      return Route.NEWS;

    case ContentType.PEOPLE_PAGE:
      return Route.PEOPLE;

    case ContentType.PRESS_ROOM_PAGE:
      return Route.PRESS_ROOM;

    case ContentType.PRIVACY_POLICY:
      return Route.PRIVACY_POLICY;

    case ContentType.ROOT_PAGE:
      invariant(slug, 'Root page must have a slug');
      return `/${slug}`;

    case ContentType.TERMS_OF_USE_PAGE:
      return Route.TERMS_OF_USE;

    case ContentType.TRUSTEE:
    case ContentType.TRUSTEES_PAGE:
      return Route.TRUSTEES;

    default:
      invariant(false, `Unknown internal page type: ${contentType}`);
  }
}
